import {
    DAY_NAMES,
    IBaseServerJobAttribute,
    IClientJobStatistics,
    IInterviewAvailability,
    IInterviewTimeslots,
    IJobApplication,
    IRequirementsData,
    IScheduleInterviewApiPayload,
    IServerJobApplication,
    IServerJobStatistics,
    IReferrer,
    IServerQuestionAnswer,
    IQuestionAnswer,
} from '../../../common';
import { getExtendedBasicUserFromServerFormat } from '../../../actions/user/saga/manager';
import { getApplyQuestionsFromServerFormat } from '../../../actions/jobs/saga/manager';

const getJobAttributeValueBasedOnName = (
    data: Array<IBaseServerJobAttribute>,
    name: string
) => {
    const result = data.find(
        (item: IBaseServerJobAttribute) =>
            item.job_attribute_option.name === name
    );

    return result ? result.job_attribute_value.name : '';
};

const convertServerJobRequirementsToClient = (
    serverRequirements: IRequirementsData
): IRequirementsData => {
    return serverRequirements;
};

const convertServerQuestionAnswersToClient = (
    serverQuestionAnswers: Array<IServerQuestionAnswer> | undefined
): Array<IQuestionAnswer> | undefined => {
    if (!serverQuestionAnswers || serverQuestionAnswers.length === 0) {
        return undefined;
    }

    return serverQuestionAnswers.map((qa) => {
        const applyQuestions = getApplyQuestionsFromServerFormat([
            qa.apply_question,
        ]);
        const applyQuestion = applyQuestions?.[0];

        return {
            id: qa.id,
            applyQuestion: applyQuestion!,
            answer: qa.answer,
            selectedOptions: qa.selected_options,
            fileUrl: qa.file_url,
            fileName: qa.file_name,
            createdAt: qa.created_at,
        };
    });
};

export const formatActiveJobApplication = (
    application: IServerJobApplication
): IJobApplication => {
    return {
        id: application.id,
        candidateScore: application.candidate_score,
        job: {
            id: Number(application.job.id),
            location: application.job.location,
            title: application.job.title,
            JobType: getJobAttributeValueBasedOnName(
                application.job.job_attributes,
                'Job Type'
            ),
        },
        resumeUrl: application.resume_url,
        status: application.status,
        submissionDate: application.submission_date,
        insights: {
            positiveInsights: application.insights.positive_insights.map(
                (insight) => insight.insight
            ),
            negativeInsights: application.insights.negative_insights.map(
                (insight) => insight.insight
            ),
        },
        comments: application.comments,
        isSaved: application.is_saved,
        requirements: convertServerJobRequirementsToClient(
            application.requirements
        ),
        steps: application.steps,
        currentStep: application.current_step,
        source: application.referral_source,
        referrer: convertServerReferrerToClient(application.referrer),
        notes: application.notes,
        tags: application.tags,
        trail: application.trail,
        chat: application.chat,
        emails: [],
        questionAnswers: convertServerQuestionAnswersToClient(
            application.question_answers
        ),
        firstName: application.first_name,
        lastName: application.last_name,
        email: application.email,
        phone: application.phone,
        isLegacy: application.is_legacy ?? false,
    };
};

export const convertServerReferrerToClient = (
    serverReferrer: IServerJobApplication['referrer']
): IReferrer => {
    return (
        serverReferrer && {
            user: getExtendedBasicUserFromServerFormat(serverReferrer.user),
            companyName: serverReferrer.company_name,
            isPointOfContact: serverReferrer.is_point_of_contact,
        }
    );
};

export const formatJobApplicationsStatistics = (
    serverJobApplicationsStatistics: IServerJobStatistics
): IClientJobStatistics => {
    return {
        totalApplicants: serverJobApplicationsStatistics.total_applicants,
        totalDifference: serverJobApplicationsStatistics.total_difference,
        newApplicants: serverJobApplicationsStatistics.new_applicants,
        newDifference: serverJobApplicationsStatistics.new_difference,
        proceededApplicants:
            serverJobApplicationsStatistics.proceeded_applicants,
        proceededDifference:
            serverJobApplicationsStatistics.proceeded_difference,
        averageJobPublish: serverJobApplicationsStatistics.average_job_publish,
        rejectedApplicants: serverJobApplicationsStatistics.rejected_applicants,
        rejectedDifference: serverJobApplicationsStatistics.rejected_difference,
    };
};

export const formatScheduleInterviewData = (
    duration: number,
    availability: IInterviewAvailability
): IScheduleInterviewApiPayload => {
    const timeslots: IInterviewTimeslots = {};

    Object.keys(availability).forEach((dayValue) => {
        const dayIndex = parseInt(dayValue);
        const dayName = DAY_NAMES[dayIndex];
        const windows = availability[dayIndex];

        timeslots[dayName] = windows.map((window) => {
            const fromMinutes = parseInt(window.from);
            const toMinutes = parseInt(window.to);

            return {
                from_hour: Math.floor(fromMinutes / 60),
                from_minutes: fromMinutes % 60,
                to_hour: Math.floor(toMinutes / 60),
                to_minutes: toMinutes % 60,
            };
        });
    });

    return {
        duration,
        timeslots,
    };
};
